<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import countries from 'shared/constants/countries';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contact: { type: Object, required: true },
  countryContext: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});
const { t, locale } = useI18n();
const store = useStore();
const editing = ref(false);
const selectedCountry = ref('');
const saving = ref(false);
const error = ref('');
let revision = 0;

const countryByCode = new Map(countries.map(country => [country.id, country]));
const manualCountry = computed(() => {
  const code = props.contact.additional_attributes?.borderchat_country_override;
  return countryByCode.has(code) ? code : '';
});
const context = computed(() =>
  manualCountry.value
    ? { source: 'manual', country_code: manualCountry.value }
    : props.countryContext || { source: 'unknown' }
);
const displayName = (code, type) => {
  try {
    return new Intl.DisplayNames([locale.value.replace('_', '-')], { type }).of(code);
  } catch {
    return countryByCode.get(code)?.name || code;
  }
};
const options = computed(() =>
  countries.map(country => ({ ...country, label: displayName(country.id, 'region') }))
    .sort((a, b) => a.label.localeCompare(b.label, locale.value.replace('_', '-')))
);
const label = computed(() => {
  if (props.loading && !manualCountry.value && !props.countryContext) return t('BORDERCHAT.COUNTRY_INFO.LOADING');
  if (countryByCode.has(context.value.country_code)) return displayName(context.value.country_code, 'region');
  if (context.value.language_code) return displayName(context.value.language_code, 'language');
  return t('BORDERCHAT.COUNTRY_INFO.UNKNOWN');
});
const flag = computed(() => countryByCode.get(context.value.country_code)?.emoji);
const sourceLabel = computed(() =>
  t(`BORDERCHAT.COUNTRY_INFO.SOURCE.${context.value.source || 'unknown'}`)
);

const edit = () => {
  selectedCountry.value = manualCountry.value || context.value.country_code || '';
  error.value = '';
  editing.value = true;
};
const save = async () => {
  if (saving.value || (selectedCountry.value && !countryByCode.has(selectedCountry.value))) return;
  const currentRevision = ++revision;
  const id = props.contact.id;
  saving.value = true;
  error.value = '';
  try {
    await store.dispatch('contacts/update', {
      id,
      additional_attributes: { borderchat_country_override: selectedCountry.value || null },
    });
    if (currentRevision === revision) editing.value = false;
  } catch {
    if (currentRevision === revision) error.value = t('BORDERCHAT.COUNTRY_INFO.SAVE_ERROR');
  } finally {
    if (currentRevision === revision) saving.value = false;
  }
};
watch(() => props.contact.id, () => {
  revision += 1;
  editing.value = false;
  saving.value = false;
  error.value = '';
});
</script>

<template>
  <div class="min-w-0 flex-1 pl-3" data-contact-country>
    <button
      type="button"
      class="flex w-full items-center gap-1 rounded-md px-1 py-0.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
      :aria-label="`${$t('BORDERCHAT.COUNTRY_INFO.EDIT')}: ${label}`"
      :aria-expanded="editing"
      @click="edit"
    >
      <span v-if="flag" aria-hidden="true">{{ flag }}</span>
      <span v-else class="i-lucide-globe size-4 shrink-0" aria-hidden="true" />
      <span class="min-w-0 break-words">{{ label }}</span>
      <span class="i-lucide-pencil size-3 shrink-0 text-n-slate-10" aria-hidden="true" />
    </button>
    <p class="mb-0 px-1 text-xs text-n-slate-10">{{ sourceLabel }}</p>
    <form v-if="editing" class="mt-2 flex flex-col gap-2" @submit.prevent="save">
      <label class="text-xs text-n-slate-11">
        {{ $t('BORDERCHAT.COUNTRY_INFO.EDIT') }}
        <select v-model="selectedCountry" class="mb-0 mt-1 w-full text-sm" :disabled="saving">
          <option value="">{{ $t('BORDERCHAT.COUNTRY_INFO.AUTO') }}</option>
          <option v-for="country in options" :key="country.id" :value="country.id">
            {{ country.emoji }} {{ country.label }}
          </option>
        </select>
      </label>
      <p v-if="error" role="alert" class="mb-0 text-xs text-n-ruby-11">{{ error }}</p>
      <div class="flex gap-1">
        <Button type="submit" :label="$t('BORDERCHAT.COUNTRY_INFO.SAVE')" :disabled="saving" xs />
        <Button type="button" :label="$t('BORDERCHAT.COUNTRY_INFO.CANCEL')" :disabled="saving" slate ghost xs @click="editing = false" />
      </div>
    </form>
  </div>
</template>
